import React, {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLInputAutoCompleteAttribute,
  useEffect
} from "react";
import arrowDownIcon from "../../assets/images/arrow-down.svg";
import { isNullOrWhiteSpace } from "../../shared/string";
import { autoCompleteSupportedFields } from "../Form/helpers";

interface ControlledSelectProps {
  autoComplete?: HTMLInputAutoCompleteAttribute;
  additionalClassName?: string;
  error?: boolean;
  focused?: boolean;
  id: string;
  label?: string;
  onBlur?: FocusEventHandler<HTMLSelectElement>;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onFocus?: FocusEventHandler<HTMLSelectElement>;
  options: {
    title: string;
    value: string;
  }[];
  readonly?: boolean;
  value: string;
  isControlled?: boolean;
}

const ControlledSelect = (props: ControlledSelectProps) => {
  const {
    autoComplete,
    additionalClassName = "",
    error,
    focused,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    options,
    readonly,
    value,
    isControlled = false
  } = props;

  let selectClasses =
    "w-full px-4 outline-none text-brand-primary-20 text-lg rounded border appearance-none";
  let labelClasses =
    "absolute left-0 ml-4 transform transition-all duration-100 -translate-y-1/2 pointer-events-none";

  if (!isNullOrWhiteSpace(value)) {
    selectClasses += " pt-9 pb-4";
    labelClasses += " top-0 mt-3 transform-none text-sm";
  } else {
    selectClasses += " py-7";
    labelClasses += " top-1/2 mt-0 text-lg";
  }

  if (!readonly && focused) {
    labelClasses += " text-brand-primary-50";
  } else {
    labelClasses += " text-label";
  }

  if (error) {
    selectClasses += " border-brand-tertiary-50 shadow-outline-error";
  } else {
    selectClasses +=
      " border-input-border focus:border-input-border-focused focus:shadow-outline";
  }

  if (readonly) {
    selectClasses += " bg-input-bg-readonly";
  } else {
    // Override default user agent stylesheet background
    selectClasses += " bg-neutral-100";
  }

  useEffect(() => {
    if (value && !readonly && onChange && isControlled) {
      const event = {
        target: {
          value
        }
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    }
  }, [isControlled, onChange, readonly, value]);

  return (
    <div className={`relative w-full ${additionalClassName}`}>
      {label && (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      )}
      <div className="absolute top-1/2 right-0 mr-4 transform -translate-y-1/2 pointer-events-none">
        <svg
          width={11}
          height={7}
          className="text-anticimex-blue fill-current stroke-current"
        >
          <use xlinkHref={arrowDownIcon.url} />
        </svg>
      </div>
      {readonly && <input value={value} id={id} name={id} type="hidden" />}
      <select
        id={id}
        autoComplete={
          autoComplete || (autoCompleteSupportedFields.includes(id) ? "on" : "off")
        }
        name={id}
        className={selectClasses}
        defaultValue={value}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        disabled={readonly}
        {...(value && !readonly && isControlled && { value })}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.title}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ControlledSelect;

